import React, { useEffect, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { TablePaginationConfig } from 'antd';
import { ColumnType } from 'antd/lib/table';
import { useUpdateEffect } from 'usehooks-ts';

import './index.less';

import {
  ELMA365ApplicationAvailability,
  ELMA365ApplicationModel,
  ELMA365ApplicationVisibility,
  ELMA365IdentityType,
  ELMA365ToolsetModel,
  ELMA365VersionAction,
} from '../../../../../../gamma-api';
import { alertsSelectorAdd } from '../../../../../recoil';
import { defaultApi } from '../../../../../apis';
import IbDebounceInput from '../../../../../components/common/IbDebounceInput';
import IbPagination from '../../../../../components/common/IbPagination';
import IbTable from '../../../../../components/common/IbTable';
import IbTypography from '../../../../../components/common/IbTypography';
import IbTextHighlighter from '../../../../../components/common/IbTextHighlighter';
import IbIcon from '../../../../../components/common/IbIcon';
import IbCheckbox from '../../../../../components/common/IbCheckbox';
import { getTranslateFunc } from '../../../../../utils/stringUtil';
import { getComponentKey } from '../utils';

import { CHECKBOX_CLASS_NAME, MAIN_CLASS_NAME, APPLICATION_PAGE_SIZE, SEARCH_CLASS_NAME } from './const';

interface IApplicationTableProps {
  action: ELMA365VersionAction;
  toolset?: ELMA365ToolsetModel;
  versionId?: string;
  toggledIds?: { [key: string]: boolean };
  onToggle?: (id: string, selected: boolean) => void;
}

const ApplicationTable: React.FC<IApplicationTableProps> = ({ action, toolset, versionId, toggledIds, onToggle }) => {
  const addAlert = useSetRecoilState(alertsSelectorAdd);

  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: APPLICATION_PAGE_SIZE,
    total: 0,
  });

  const [searchText, setSearchText] = useState('');
  const [applications, setApplications] = useState<ELMA365ApplicationModel[]>([]);

  const [loading, setLoading] = useState(true);

  const translate = getTranslateFunc('Toolkits: ELMA365: Applications:');

  const selectedKeys = new Set();
  if (toolset) {
    for (const namespace of toolset.namespaces) {
      for (const component of namespace.components) {
        if (component.identity.type === ELMA365IdentityType.Application) {
          const key = getComponentKey(namespace, component);
          selectedKeys.add(key);
        }
      }
    }
  }

  const isSelectionDisabled = (application: ELMA365ApplicationModel) => {
    if (action !== ELMA365VersionAction.Appending) {
      return false;
    }

    const key = getComponentKey(application.namespace, application);
    return selectedKeys.has(key);
  };

  const isApplicationSelected = (application: ELMA365ApplicationModel) => {
    return (
      toggledIds?.[application.application_id] ?? application.availability === ELMA365ApplicationAvailability.Enabled
    );
  };

  const loadApplications = async () => {
    if (!versionId) {
      setApplications([]);
      return;
    }

    setLoading(true);

    try {
      const response = await defaultApi.searchELMA365ToolkitApplications(
        (pagination.current || 1) - 1,
        APPLICATION_PAGE_SIZE,
        true,
        searchText,
        undefined,
        undefined,
        [versionId],
        undefined,
        undefined,
        [ELMA365ApplicationVisibility.Visible]
      );
      setApplications(response.data.items || []);
      setPagination((prev) => ({ ...prev, total: response.data.total_item_count || 0 }));
    } catch (err) {
      addAlert({
        content: translate('Failed to load'),
        type: 'error',
      });
      // eslint-disable-next-line no-console
      console.error(err);
    }

    setLoading(false);
  };

  useEffect(() => {
    loadApplications().finally();
  }, [versionId, pagination.current, pagination.pageSize, searchText]);

  useUpdateEffect(() => {
    if (pagination.current !== 1) {
      setPagination({ ...pagination, current: 1 });
    }
  }, [versionId, searchText]);

  const onPaginationChange = (page: number) => setPagination({ ...pagination, current: page });

  const onSearchTextChange = (value: string) => setSearchText(value);

  const onApplicationToggle = (application: ELMA365ApplicationModel) => {
    if (isSelectionDisabled(application)) {
      return;
    }
    onToggle?.(application.application_id, !isApplicationSelected(application));
  };

  const renderSearch = () => {
    return (
      <div className={SEARCH_CLASS_NAME}>
        <IbDebounceInput
          allowClear
          disabled={!versionId}
          placeholder={translate('Search: Placeholder')}
          prefix={<IbIcon iconName="search" />}
          onDebouncedChange={onSearchTextChange}
        />
      </div>
    );
  };

  const renderCheckbox = (application: ELMA365ApplicationModel) => {
    const onCheckboxChange = () => onApplicationToggle(application);

    return (
      <div className={CHECKBOX_CLASS_NAME}>
        <IbCheckbox
          disabled={isSelectionDisabled(application)}
          value={isApplicationSelected(application)}
          onChange={onCheckboxChange}
        />
        <IbTypography.Paragraph ellipsis type="secondary">
          <IbTextHighlighter highlightedText={searchText} text={application.identity.name} />
        </IbTypography.Paragraph>
      </div>
    );
  };

  const renderTable = () => {
    const columns: ColumnType<ELMA365ApplicationModel>[] = [
      {
        title: translate('Table: Name'),
        width: '36%',
        ellipsis: true,
        render: (_, application) => renderCheckbox(application),
      },
      {
        title: translate('Table: Code'),
        width: '32%',
        ellipsis: true,
        render: (_, application) => (
          <IbTypography.Paragraph ellipsis type="secondary">
            <IbTextHighlighter
              highlightedText={searchText}
              text={`${application.namespace.identity.code} / ${application.identity.code}`}
            />
          </IbTypography.Paragraph>
        ),
      },
      {
        title: translate('Table: Namespace'),
        width: '32%',
        ellipsis: true,
        render: (_, application) => (
          <IbTypography.Paragraph ellipsis type="secondary">
            <IbTextHighlighter highlightedText={searchText} text={application.namespace.identity.name} />
          </IbTypography.Paragraph>
        ),
      },
    ];

    return (
      <IbTable
        columns={columns}
        dataSource={applications}
        emptyText={<IbTypography.Paragraph disabled>{translate('Nothing found')}</IbTypography.Paragraph>}
        loading={!versionId || loading}
        rowKey={(application: ELMA365ApplicationModel) => application.application_id}
        size="middle"
        onRowClick={onApplicationToggle}
      />
    );
  };

  const renderPagination = () => {
    if ((pagination.total ?? 0) <= APPLICATION_PAGE_SIZE) {
      return null;
    }

    return (
      <IbPagination
        current={pagination.current}
        defaultCurrent={1}
        defaultPageSize={APPLICATION_PAGE_SIZE}
        pageSize={APPLICATION_PAGE_SIZE}
        showSizeChanger={false}
        total={pagination.total}
        onChange={onPaginationChange}
      />
    );
  };

  return (
    <div className={MAIN_CLASS_NAME}>
      {renderSearch()}
      {renderTable()}
      {renderPagination()}
    </div>
  );
};

export default ApplicationTable;
